/*
 * CareIQ N3 rollout flag (BUILD-PLAN §3 "Flag", CONTRACT §2).
 *
 * The new CareIQ tab (tabs/careiq/CareIQTabN3.jsx) and its chart frame are
 * switched on one login at a time by a server file that v2 serves at
 *   GET /v1/kg/{practiceid}/careiq/ui-flag  ->  {"ui": "n3" | "legacy"}
 * Only a practice whose profile sets practiceFeatures().careiqN3 (9928: "legacy"
 * = the old CareIQ tab) or whose CareIQ tab is N3-only (careiqTabN3Only(), 7825:
 * "legacy" = no CareIQ tab) asks; everyone else is "legacy" without a request.
 * The chart's tab list reads the chart window's own copy of the answer
 * (useCareiqN3Chart -> config/practice.js chartTabAllowed(11, n3)).
 *
 * Fail closed: any non-200, network error, timeout (5 s) or a body other than
 * {"ui":"n3"} means "legacy". The value is fetched after login (App.js) and
 * re-checked every 5 minutes. A chart reads it once when it opens and keeps
 * it until it is closed (useCareiqN3Chart), so a flag change never swaps a
 * tab or the frame under an open chart.
 *
 * States: "unknown" (no answer yet), "n3", "legacy".
 */
import { useEffect, useState } from "react";
import careiqapi from "../api/careiqapi";
import { careiqTabN3Only, getPracticeId, practiceFeatures } from "../config/practice";
import { careiqPreviewMode } from "./careiqPreview";

const POLL_MS = 5 * 60 * 1000;
const TIMEOUT_MS = 5000;

let current = "unknown";
let fetchedForToken = null;
let inflight = null;
let inflightToken = null;
let pollTimer = null;
const listeners = new Set();

const readToken = () => {
  try {
    return localStorage.getItem("auth");
  } catch (_) {
    return null;
  }
};

const publish = (v) => {
  if (v === current) return;
  current = v;
  listeners.forEach((fn) => {
    try { fn(v); } catch (_) { /* a listener never breaks the others */ }
  });
};

// The practice can have N3 at all (9928; 7825's N3-only CareIQ tab). Read at
// call time: the practice id is a runtime value (window.__CARE_CONFIG__), not a
// build constant. The specialty preview (utils/careiqPreview.js, a localStorage
// key) lets any practice ask; v2 answers "n3" to a preview request from bmadmin
// only (anyone else gets the plain answer).
export function careiqN3Available() {
  return Boolean(practiceFeatures().careiqN3) || careiqTabN3Only() || careiqPreviewMode() !== null;
}

// Strict reading of the answer: exactly {"ui":"n3"} turns N3 on.
export function flagFromBody(body) {
  return body && typeof body === "object" && !Array.isArray(body) && body.ui === "n3" ? "n3" : "legacy";
}

export function getCareiqN3Flag() {
  return careiqN3Available() ? current : "legacy";
}

export function refreshCareiqN3Flag() {
  if (!careiqN3Available()) {
    publish("legacy");
    return Promise.resolve("legacy");
  }
  const token = readToken();
  if (!token) {
    fetchedForToken = null;
    inflight = null;
    inflightToken = null;
    publish("legacy");
    return Promise.resolve("legacy");
  }
  if (inflight && inflightToken === token) return inflight;
  // Another sign-in on this tab (logout is an SPA navigation, so this module
  // keeps the last login's answer) never inherits it: "unknown" (= legacy
  // at chart open) until this token's own answer arrives.
  if (token !== fetchedForToken) publish("unknown");
  fetchedForToken = token;
  const preview = careiqPreviewMode();
  const opts = preview ? { timeout: TIMEOUT_MS, params: { careiq_preview: preview } } : { timeout: TIMEOUT_MS };
  const mine = careiqapi
    .get(`/v1/kg/${getPracticeId()}/careiq/ui-flag`, opts)
    .then(flagFromBody, () => "legacy")
    .then((v) => {
      if (inflight === mine) {
        inflight = null;
        inflightToken = null;
      }
      // An answer for a token that is no longer the signed-in one is dropped.
      if (readToken() === token) publish(v);
      return v;
    });
  inflight = mine;
  inflightToken = token;
  return mine;
}

// After login (and on every page change, cheaply): asks at once when there is
// no answer yet or the signed-in token changed, then every 5 minutes.
export function startCareiqN3Flag() {
  if (!careiqN3Available()) return;
  if (current === "unknown" || readToken() !== fetchedForToken) refreshCareiqN3Flag();
  if (!pollTimer) pollTimer = setInterval(refreshCareiqN3Flag, POLL_MS);
}

export function subscribeCareiqN3Flag(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function resetCareiqN3FlagForTests() {
  current = "unknown";
  fetchedForToken = null;
  inflight = null;
  inflightToken = null;
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  listeners.clear();
}

/*
 * The flag as one chart window sees it: "n3" or "legacy", read when `open`
 * turns true and kept until the window closes, so a flag change never swaps
 * the tab or the frame under an open chart. No answer yet ("unknown") means
 * legacy for this chart (fail closed); the flag is asked for after login, so
 * by the time a chart opens it has normally arrived, and the next chart open
 * uses it in any case.
 */
export function useCareiqN3Chart(open = true) {
  const available = careiqN3Available();
  const read = () => (open && available && getCareiqN3Flag() === "n3" ? "n3" : "legacy");
  const [lock, setLock] = useState(() => ({ open, value: read() }));
  const stale = lock.open !== open;
  if (stale) setLock({ open, value: read() });

  useEffect(() => {
    if (available && open) startCareiqN3Flag();
  }, [available, open]);

  return stale ? read() : lock.value;
}
